import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';
import { clearReauthCooldown, REAUTH_RETURN_KEY, setTenantAccessDeniedHandler } from '@/shared/api';
import { env } from '@/shared/config/env';
import { publishGlobalFeedback } from '@/shared/feedback';
import i18n from '@/shared/i18n/i18n';
import { LOCALE_STORAGE_KEY, SUPPORTED_LOCALES } from '@/shared/i18n/locales';

import { fetchMe, putActiveTenant, redirectToLogin, submitLogout } from './sessionApi';
import type { MeResponse, SessionContextValue, SessionStatus, SessionTenant } from './sessionTypes';

// Context co-located with its provider (matching the project's other contexts);
// the hook lives in useSession.ts.
// eslint-disable-next-line react-refresh/only-export-components
export const SessionContext = createContext<SessionContextValue | undefined>(undefined);

const SESSION_QUERY_KEY = ['session', 'me'] as const;

/**
 * Tenant isolation (not just UX): drop every cached query except the session itself, so a
 * mounted screen cannot go on rendering a no-longer-relevant tenant's data. Shared by an
 * explicit switch (`tenantMutation.onSuccess`) AND the `TENANT_ACCESS_DENIED` recovery path
 * (whenever it lands on a DIFFERENT tenant than before — the revoked one may still have
 * screens showing data fetched before access was pulled). See `tenantMutation.onSuccess`
 * below for why `resetQueries`, not `removeQueries`, is required here.
 */
async function resetTenantScopedQueries(queryClient: QueryClient): Promise<void> {
  await queryClient.resetQueries({
    predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0],
  });
}

// ── Mock session (offline dev + tests) ──────────────────────────────────────────

const MOCK_TENANTS: SessionTenant[] = [
  { id: env.defaultTenantId, name: 'Demo Tenant' },
  { id: 'northwind', name: 'Northwind Trading' },
  { id: 'globex', name: 'Globex Retail' },
];

const MOCK_ME: MeResponse = {
  user: { id: 'usr_demo_001', name: 'Avery Stone', email: 'avery.stone@demo-tenant.io' },
  tenants: MOCK_TENANTS,
  activeTenant: MOCK_TENANTS[0] ?? null,
  roles: ['ADMIN'],
  permissions: [
    'settings.*',
    // Both staff tiers: the mock session stands in for an administrator, and the catalogue emits the
    // read code alongside the manage code at ADMIN. Without `employees.view` here the staff directory —
    // now gated on it — would 'Access restricted' in offline dev and in every test rendering the app.
    'employees.view',
    'employees.manage',
    'roles.manage',
    'products.view',
    'catalog.view',
    'catalog.manage',
    'customers.view',
    'customers.manage',
    'catalog.pricing.view',
    'catalog.pricing.manage',
    'catalog.structure.view',
    'catalog.structure.manage',
    'loyalty.view',
    'loyalty.manage',
    'inventory.view',
    'inventory.manage',
    'catalog.promo.view',
    'catalog.promo.manage',
    'catalog.promo.approve',
    'catalog.budgets.manage',
    'catalog.publication.view',
    'catalog.publication.manage',
    // Evotor process-sync integration — bare `integrations.evotor.*` codes, not under `settings.*`
    // (see `store-settings/model/permissions.ts`'s `evotor-integration` entry).
    'integrations.evotor.view',
    'integrations.evotor.manage',
    // The omnichannel conversation inbox + chatbot behaviour settings (plan-conversation-001
    // WP-MP.0). Neither has a screen yet (`conversations` ships in WP-MP.5, `chatbot` in WP-MP.4;
    // `merchantPortal.nav.conversations` also stays off by default — see
    // `merchantPortalNavVisibility.ts`), so granting these codes in the mock session today is inert —
    // it only lets local dev/tests exercise the permission plumbing ahead of the UI that reads it.
    // `settings.chatbot.*` is already covered by the `settings.*` wildcard above; listed explicitly
    // anyway so a reader of this file doesn't have to know that to find it granted.
    'conversations.view',
    'conversations.manage',
    // Canonical capability-code family (`<domain>.feature.<capability>` — see
    // `shared/access/accessCodes.ts`'s `featureCode`): may this operator reply from the inbox.
    'conversations.feature.operator-reply',
    'settings.chatbot.view',
    'settings.chatbot.manage',
    // The chatbot templates & FAQ editor (plan-conversation-001 WP-MP.2) reuses the `content`
    // domain rather than `settings.chatbot.*` (adr-auth-006 D5) — outside the `settings.*` wildcard
    // above, so it needs its own explicit grant, same as every other bare-namespace domain in this
    // list (`conversations.*`, `integrations.evotor.*`). Unlike `conversations`/`settings.chatbot`
    // above, this one is NOT inert: `/settings/chatbot/templates` is a real, routed screen from this
    // WP onward.
    'content.view',
    'content.manage',
    // Canonical capability-code family: may this operator publish (save/reset/accept-default/CRUD
    // FAQ) chatbot texts, distinct from merely viewing them — adr-auth-006 D5.
    'content.feature.publish',
  ],
  // Lets local dev/tests simulate a platform manager's merchant-portal feature-flag settings
  // (plan-tenant-003) without a backend — see `env.mockFeatureFlags` (parsed from
  // `VITE_MOCK_FEATURE_FLAGS`). `null` when unset/invalid, matching the real BFF's own
  // fail-open behaviour (the SPA's `flag ?? codeDefault` resolvers then apply unchanged).
  featureFlags: env.mockFeatureFlags,
  // Deliberately `null` ("unknown"), not `false` ("not enrolled") — see
  // `SessionContextValue.passkeyEnrolled`. Keeps the post-login nudge (which requires a definite
  // `false`) out of every existing test and every offline `mock`-mode session, exactly as
  // user-story-merchant-portal-060 AC14 requires.
  passkeyEnrolled: null,
};

function MockSessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse>(MOCK_ME);

  const setActiveTenant = useCallback(async (tenantId: string) => {
    setMe((current) => ({
      ...current,
      activeTenant: current.tenants.find((t) => t.id === tenantId) ?? current.activeTenant,
    }));
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      status: 'authenticated',
      user: me.user,
      tenants: me.tenants,
      activeTenant: me.activeTenant,
      roles: me.roles,
      permissions: me.permissions,
      featureFlags: me.featureFlags ?? null,
      passkeyEnrolled: me.passkeyEnrolled ?? null,
      isAuthenticated: true,
      login: () => {},
      logout: () => setMe((c) => ({ ...c, user: { ...c.user } })),
      setActiveTenant,
      refresh: async () => {},
    }),
    [me, setActiveTenant],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

// ── Real (BFF) session ──────────────────────────────────────────────────────────

/**
 * Map a tenant locale string (e.g. 'ru-RU', 'en-US', 'zh-CN') to a supported
 * i18next locale, or null if there is no suitable match.
 */
function normalizeTenantLocale(tenantLocale: string): string | null {
  // Direct match first (e.g. 'ru', 'en', 'zh-CN', 'zh-TW')
  if ((SUPPORTED_LOCALES as readonly string[]).includes(tenantLocale)) return tenantLocale;
  // Derive from language + region: 'ru-RU' → 'ru', 'en-US' → 'en', 'zh-TW' → 'zh-TW'
  const [lang = '', region = ''] = tenantLocale.split('-');
  if (lang === 'zh') return region === 'TW' || region === 'HK' ? 'zh-TW' : 'zh-CN';
  if ((SUPPORTED_LOCALES as readonly string[]).includes(lang)) return lang;
  return null;
}

function BffSessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery<MeResponse, NormalizedApiError>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchMe,
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // After a mid-session 401 the apiClient redirects to Keycloak and saves the
  // current path in sessionStorage. Once the user is silently re-authenticated
  // and the SPA boots at the root, restore navigation to the original path.
  // The ref guards against firing the replace twice if the effect re-runs.
  const returnPathHandled = useRef(false);
  useEffect(() => {
    if (meQuery.isSuccess && !returnPathHandled.current) {
      returnPathHandled.current = true;
      // The round-trip worked, so the next expiry gets its own transparent re-auth
      // instead of being suppressed by this attempt's leftover loop-breaker.
      clearReauthCooldown();
      const returnPath = sessionStorage.getItem(REAUTH_RETURN_KEY);
      sessionStorage.removeItem(REAUTH_RETURN_KEY);
      // Re-validate on read, not only on write: sessionStorage is attacker-writable from
      // any script on this origin, and `location.replace` on a protocol-relative value
      // ("//evil.com") would navigate off-site.
      if (returnPath && returnPath.startsWith('/') && !returnPath.startsWith('//')) {
        window.location.replace(returnPath);
      }
    }
  }, [meQuery.isSuccess]);

  // Sync the tenant's preferred locale to i18next when the session loads,
  // but only if the user has not explicitly chosen their own locale preference.
  useEffect(() => {
    if (meQuery.isSuccess && meQuery.data) {
      const tenantLocale = meQuery.data.activeTenant?.locale;
      const userPreference = localStorage.getItem(LOCALE_STORAGE_KEY);
      // Only apply tenant locale if user hasn't explicitly chosen their own
      if (!userPreference && tenantLocale) {
        const normalized = normalizeTenantLocale(tenantLocale);
        if (normalized) void i18n.changeLanguage(normalized);
      }
    }
  }, [meQuery.isSuccess, meQuery.data]);

  const tenantMutation = useMutation<MeResponse, NormalizedApiError, string>({
    mutationFn: putActiveTenant,
    onSuccess: async (data) => {
      // 1) Update the session cache. `activeTenant`/`tenants`/`roles`/`permissions` are now the
      //    new tenant's. Note there is no client-held `X-Tenant-Id` to resynchronise first: the
      //    BFF already scoped the *server-side* session to the new tenant while handling this PUT
      //    (this response IS that switch), and `apiClient` cannot attach a tenant header at all
      //    (the deprecated `setApiTenantId` hatch was removed by plan-cleanup-001 — see
      //    `apiClient.test.ts`), so every outgoing request is already answered under the new
      //    tenant purely via the session cookie by the time this callback runs. So this write
      //    only has to satisfy the UI.
      queryClient.setQueryData(SESSION_QUERY_KEY, data);

      // 2) Tenant isolation (not just UX): drop every OTHER cached query so a mounted screen
      //    cannot go on rendering the previous tenant's data even for a moment. `resetQueries` —
      //    not `removeQueries` — is required here: `removeQueries` deletes the cache entry but
      //    does not itself notify a query's currently-mounted observers (confirmed by reading
      //    @tanstack/query-core's `Query.destroy()`/`QueryObserver` — a still-rendered `useQuery`
      //    keeps returning its last, stale, "success" result until something unrelated happens to
      //    re-render that component). `resetQueries` synchronously resets every matching query to
      //    its pristine pending/no-data state (observers ARE notified — the screen goes to
      //    loading) and then refetches the ones that are currently active/mounted under the new
      //    tenant — see SessionContext.bff.test.tsx. Every feature deliberately roots its query
      //    keys under one literal precisely for this ("so a tenant switch can drop it all" —
      //    catalogKeys/settingsKeys/inventoryKeys/promoKeys); `['session', 'me']` is the only key
      //    that must survive.
      await resetTenantScopedQueries(queryClient);
    },
  });

  const setActiveTenant = useCallback(
    async (tenantId: string) => {
      await tenantMutation.mutateAsync(tenantId);
    },
    [tenantMutation],
  );

  // Recovery from a mid-session TENANT_ACCESS_DENIED (403): the active tenant became
  // inaccessible (e.g. membership revoked by an admin while the user was still in it).
  // Registered on `apiClient` (outside React) so its response interceptor can reach in;
  // see `setTenantAccessDeniedHandler`. Guarded so a burst of failing requests (a query's
  // own default retry included) only refreshes/notifies once per window, not per request.
  const lastTenantAccessDeniedAtRef = useRef(0);
  useEffect(() => {
    const TENANT_ACCESS_DENIED_GUARD_MS = 5_000;

    const handleTenantAccessDenied = () => {
      const now = Date.now();
      if (now - lastTenantAccessDeniedAtRef.current < TENANT_ACCESS_DENIED_GUARD_MS) {
        return;
      }
      lastTenantAccessDeniedAtRef.current = now;

      // Refresh the session: the BFF's `/me` reassigns a valid tenant (its own default-tenant
      // fallback), and the refreshed `tenants`/`activeTenant` flow to every consumer as usual.
      // If that lands on a DIFFERENT tenant than before, apply the same reset as an explicit
      // switch: screens may still hold data fetched under the just-revoked tenant, which is the
      // same tenant-isolation concern as `tenantMutation.onSuccess`, not merely a UX nicety. If
      // it's the SAME tenant (e.g. a transient membership-check hiccup), leave the cache alone —
      // nothing became stale.
      void (async () => {
        const previousTenantId =
          queryClient.getQueryData<MeResponse>(SESSION_QUERY_KEY)?.activeTenant?.id ?? null;
        await queryClient.refetchQueries({ queryKey: SESSION_QUERY_KEY, exact: true });
        const nextTenantId =
          queryClient.getQueryData<MeResponse>(SESSION_QUERY_KEY)?.activeTenant?.id ?? null;
        if (nextTenantId !== previousTenantId) {
          await resetTenantScopedQueries(queryClient);
        }
      })();

      publishGlobalFeedback({
        severity: 'warning',
        message: i18n.t('error.tenantAccessDenied', {
          defaultValue:
            'You no longer have access to the selected company. Your company list was refreshed.',
        }),
      });
    };

    setTenantAccessDeniedHandler(handleTenantAccessDenied);
    return () => setTenantAccessDeniedHandler(null);
  }, [queryClient]);

  const refresh = useCallback(async () => {
    await meQuery.refetch();
  }, [meQuery]);

  const status: SessionStatus = meQuery.isLoading
    ? 'loading'
    : meQuery.isSuccess
      ? 'authenticated'
      : 'unauthenticated';

  const me = meQuery.data;

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      user: me?.user ?? null,
      tenants: me?.tenants ?? [],
      activeTenant: me?.activeTenant ?? null,
      roles: me?.roles ?? [],
      permissions: me?.permissions ?? [],
      featureFlags: me?.featureFlags ?? null,
      // `me?.passkeyEnrolled` is `undefined` both while loading and when the BFF's own
      // `passkeyEnrolled` support is off — either way that normalizes to `null` ("unknown"), never
      // to `false` ("not enrolled"). See `SessionContextValue.passkeyEnrolled`.
      passkeyEnrolled: me?.passkeyEnrolled ?? null,
      isAuthenticated: status === 'authenticated',
      login: redirectToLogin,
      logout: submitLogout,
      setActiveTenant,
      refresh,
    }),
    [status, me, setActiveTenant, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Provides the application session. Backed by `GET /api/v1/me` (BFF cookie session)
 * in `bff` mode, or an in-browser mock in `mock` mode (default — offline dev + tests).
 * Must sit inside the TanStack Query provider.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  if (env.authMode === 'bff') {
    return <BffSessionProvider>{children}</BffSessionProvider>;
  }
  return <MockSessionProvider>{children}</MockSessionProvider>;
}
